import { get, omitBy } from "lodash/fp";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Content,
  FlexboxGrid,
  Footer,
  Header,
  Table,
  Input,
  InputGroup,
  Loader,
  Form,
} from "rsuite";
import SearchIcon from "@rsuite/icons/Search";
import CloseIcon from "@rsuite/icons/Close";
import { StoreScheduleServices } from "../../../core/services/StoreScheduleServices";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreScheduleListParams } from "./StoreScheduleDataTypes";
import "./StoreSchedules.css";
import StoreScheduleFilters from "./StoreScheduleFilters";
import {
  displayDateField,
  getDateShortDay,
  getTodayShortDay,
  isEmptyVal,
} from "../../../utils/helpers";
import { ToastContainer } from "react-toastify";
import {
  roleTypeArr,
  statusArr,
  messageTypeArr,
  appConstObj,
  scheduleForTime,
  dateFormatObj,
  storeScheduleItems,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { displayField } from "../../../utils/helpers";
import MusicScheduleInfoCell from "../../components/Elements/MusicScheduleInfoCell";
import moment from "moment";
import MusicScheduleSongs from "../MusicSchedule/MusicScheduleSongs";
import CommercialScheduleItems from "../CommercialSchedule/CommercialScheduleItems";
import FilteredCharts from "../Charts/FilteredCharts";
import PlayerStopTime from "./PlayerStopTime";
import { AlertModal } from "../../components/Modals/AlertModal/AlertModal";

interface ImageModal {
  openModal: () => void;
  removePic: () => void;
  hideDelete: () => void;
}

function StoreSchedule() {
  const { t } = useTranslation();
  const { Cell, Column } = Table;
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [scheduleData, setScheduleData] = useState<any>(null);
  const [scheduleFetched, setScheduleFetched] = useState<boolean>(false);
  const company = useSelector((state: any) => state.company);

  const [tabView, setTabView] = useState(storeScheduleItems.music.value);

  const initSearchFormData = {
    schedule_date: new Date(moment().format(dateFormatObj.moment_y_m_d)),
    store: UtilityServices.getLoggedInUser("store_id"),
  };

  const [formData, setFormData] =
    useState<StoreScheduleListParams>(initSearchFormData);

  const [openScheduleDownloadModal, setOpenScheduleDownloadModal] =
    useState(false);

  async function onDownloadStoreSchedule() {
    try {
      let params = getSearchParams();
      params.with_chart_songs = "1";
      await StoreScheduleServices.downloadStoreSchedule(getSearchParams());
    } catch (error) {
      console.log(error);
    }
  }
  async function getStoreSchedule(params: StoreScheduleListParams) {
    try {
      setScheduleData(null);
      setScheduleFetched(false);
      if (!params.store) {
        UtilityServices.showToaster({
          message: t("PLEASE_SELECT_FIELD", { field: t("STORE") }),
          type: messageTypeArr.warning,
        });
        return;
      }
      setLoadingSchedule(true);

      const res = await StoreScheduleServices.getStoreScheduleFromAdmin(params);
      const resData = get("data.data", res);
      setScheduleData(resData);
      setScheduleFetched(true);
      setLoadingSchedule(false);
    } catch (error) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
      setLoadingSchedule(false);
    }
  }

  async function init() {
    try {
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  function getSearchParams(data?: any) {
    let paramsData = {
      ...formData,
      ...data,
    };

    let params: StoreScheduleListParams = omitBy(isEmptyVal, paramsData);
    return params;
  }

  function getCompanyId() {
    let compId = null;
    if (UtilityServices.getLoggedInUser("user_type") === roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (company?.id) {
      compId = company.id;
    }
    return compId;
  }

  function submitSearch(formData: any) {
    setFormData(formData);
    getStoreSchedule(getSearchParams(formData));
  }

  function clearSearch() {
    setFormData(initSearchFormData);
    getStoreSchedule(
      getSearchParams({
        ...initSearchFormData,
      }),
    );
  }

  useEffect(() => {
    getStoreSchedule(
      getSearchParams({
        schedule_date: new Date(moment().format(dateFormatObj.moment_y_m_d)),
        store: UtilityServices.getLoggedInUser("store_id"),
      }),
    );
  }, [company]);

  return (
    <div className="store-schedule customers">
      <Container className="container">
        <Header className="header">
          <FlexboxGrid justify="space-between">
            <FlexboxGrid.Item colspan={8}>
              <h4>{t("STORE_SCHEDULE")}</h4>
            </FlexboxGrid.Item>
          </FlexboxGrid>
        </Header>
        <Content>
          <StoreScheduleFilters onSubmit={submitSearch} onClear={clearSearch} />
          {loadingSchedule && (
            <Loader
              style={{ zIndex: 9999 }}
              size="lg"
              content={t("Loading...")}
              center
              backdrop={true}
            />
          )}

          <div className={null}>
            {scheduleFetched && (
              <>
                <div className="content mb-3 pb-0">
                  <FlexboxGrid justify="space-between">
                    <FlexboxGrid.Item>
                      <Form.Group controlId="has_music_schedule">
                        <Form.ControlLabel>
                          {t("HAS")} {t("MUSIC_SCHEDULE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.music_schedule !== null
                            ? t("YES")
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="playlist_title">
                        <Form.ControlLabel>
                          {t("PLAYLIST")} {t("TITLE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {displayField(
                            scheduleData?.music_schedule?.playlist_title,
                          )}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="date_range">
                        <Form.ControlLabel>
                          {t("MUSIC")} {t("AVAILABILITY")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.music_schedule?.is_default_playlist ==
                          "1"
                            ? t("ALWAYS")
                            : displayDateField(
                                scheduleData?.music_schedule?.play_start_date,
                              ) +
                              t(" ") +
                              t("TO") +
                              displayDateField(
                                scheduleData?.music_schedule?.play_end_date,
                              )}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                  <FlexboxGrid justify="space-between">
                    <FlexboxGrid.Item>
                      <Form.Group controlId="is_default_playlist">
                        <Form.ControlLabel>
                          {t("IS")} {t("DEFAULT")} {t("SCHEDULE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.music_schedule?.is_default_playlist ==
                          "1"
                            ? t("YES")
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="day_parts">
                        <Form.ControlLabel>
                          {t("DAY_PARTS")} : {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {displayField(
                            scheduleData?.music_schedule?.day_parts,
                          )}{" "}
                          (
                          {displayField(
                            scheduleData?.music_schedule?.total_songs,
                          ) +
                            t(" ") +
                            t("SONGS")}
                          )
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="new_schedule_everyday">
                        <Form.ControlLabel>
                          {t("NEW")} {t("SONG")} {t("EVERYDAY")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.music_schedule
                            ?.new_schedule_everyday == "1"
                            ? t("YES")
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                  <br />
                  <FlexboxGrid justify="space-between">
                    <FlexboxGrid.Item>
                      <Form.Group controlId="has_commercial_schedule">
                        <Form.ControlLabel>
                          {t("HAS")} {t("COMMERCIAL_SCHEDULE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.commercial_schedule !== null
                            ? t("YES") +
                              " (" +
                              displayField(
                                scheduleData?.commercial_schedule?.total_items,
                              ) +
                              " " +
                              t("ITEMS") +
                              ")"
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="commercial_schedule_title">
                        <Form.ControlLabel>
                          {t("COMMERCIAL")} {t("TITLE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {displayField(
                            scheduleData?.commercial_schedule?.schedule_title,
                          )}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="commercial_date_range">
                        <Form.ControlLabel>
                          {t("DATE")} {t("RANGE")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {displayDateField(
                            scheduleData?.commercial_schedule
                              ?.schedule_date_from,
                          )}{" "}
                          {t("TO")}{" "}
                          {displayDateField(
                            scheduleData?.commercial_schedule
                              ?.schedule_date_from,
                          )}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                  <FlexboxGrid justify="space-between">
                    <FlexboxGrid.Item>
                      <Form.Group controlId="new_schedule_everyday">
                        <Form.ControlLabel>
                          {t("NEW")} {t("COMMERCIAL")} {t("EVERYDAY")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.commercial_schedule
                            ?.commercial_schedule == "1"
                            ? t("YES")
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="has_event_play_list">
                        <Form.ControlLabel>
                          {t("EVENT")} {t("PLAYLIST")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {+scheduleData?.event_charts}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item>
                      <Form.Group controlId="has_player_stop_time">
                        <Form.ControlLabel>
                          {t("HAS")} {t("PLAYER")} {t("STOPS")}: {t(" ")}
                        </Form.ControlLabel>
                        <Form.ControlLabel>
                          {scheduleData?.player_stop_time !== null
                            ? t("YES")
                            : t("NO")}
                        </Form.ControlLabel>
                      </Form.Group>
                    </FlexboxGrid.Item>
                  </FlexboxGrid>
                </div>
                <div>
                  {scheduleData?.music_schedule !== null && (
                    <button
                      style={{
                        opacity:
                          tabView === storeScheduleItems.music.value ? 1 : 0.5,
                      }}
                      className="tab-button"
                      onClick={(e) =>
                        setTabView(storeScheduleItems.music.value)
                      }
                    >
                      {storeScheduleItems.music.label}
                    </button>
                  )}

                  {scheduleData?.commercial_schedule !== null && (
                    <button
                      style={{
                        opacity:
                          tabView === storeScheduleItems.commercial.value
                            ? 1
                            : 0.5,
                      }}
                      className="tab-button"
                      onClick={(e) =>
                        setTabView(storeScheduleItems.commercial.value)
                      }
                    >
                      {storeScheduleItems.commercial.label}
                    </button>
                  )}
                  {+scheduleData?.event_charts > 0 && (
                    <button
                      style={{
                        opacity:
                          tabView === storeScheduleItems.event_play_list.value
                            ? 1
                            : 0.5,
                      }}
                      className="tab-button"
                      onClick={(e) =>
                        setTabView(storeScheduleItems.event_play_list.value)
                      }
                    >
                      {storeScheduleItems.event_play_list.label}
                    </button>
                  )}
                  {scheduleData?.player_stop_time !== null && (
                    <button
                      style={{
                        opacity:
                          tabView === storeScheduleItems.player_stop_time.value
                            ? 1
                            : 0.5,
                      }}
                      className="tab-button"
                      onClick={(e) =>
                        setTabView(storeScheduleItems.player_stop_time.value)
                      }
                    >
                      {storeScheduleItems.player_stop_time.label}
                    </button>
                  )}
                </div>
                <div>
                  {tabView == storeScheduleItems.music.value &&
                    scheduleData?.music_schedule !== null && (
                      <MusicScheduleSongs
                        musicScheduleId={scheduleData?.music_schedule?.id}
                        songSearchParams={{
                          play_for_day: getDateShortDay(
                            scheduleData?.schedule_date,
                          ),
                        }}
                        enableBulkAction={["none"]}
                        enableDayFilter={false}
                      />
                    )}
                  {tabView == storeScheduleItems.commercial.value &&
                    scheduleData?.commercial_schedule !== null && (
                      <CommercialScheduleItems
                        commercialScheduleId={
                          scheduleData?.commercial_schedule?.id
                        }
                        itemSearchParams={{
                          play_for_day: getDateShortDay(
                            scheduleData?.schedule_date,
                          ),
                        }}
                        enableBulkAction={["none"]}
                        enableDayFilter={false}
                      />
                    )}
                  {tabView == storeScheduleItems.event_play_list.value &&
                    +scheduleData?.event_charts > 0 && (
                      <>
                        <FilteredCharts
                          filterParams={{
                            company: scheduleData?.company,
                            include_store_group: scheduleData?.store_group,
                            for: "master, company",
                            type: "event",
                          }}
                          withHeader={false}
                          enableSongBulkAction={["none"]}
                          enableChartBulkAction={["none"]}
                        />
                      </>
                    )}
                  {tabView == storeScheduleItems.player_stop_time.value &&
                    scheduleData?.player_stop_time !== null && (
                      <PlayerStopTime
                        playerStopData={scheduleData?.player_stop_time}
                        withHeader={true}
                      />
                    )}
                </div>
                <Button onClick={() => setOpenScheduleDownloadModal(true)}>
                  {t("DOWNLOAD")} {t("ACTUAL")} {t("STORE")} {t("RESPONSE")}
                </Button>
              </>
            )}
          </div>

          <ToastContainer />
        </Content>
      </Container>
      <AlertModal
        open={openScheduleDownloadModal}
        handleClose={(e: any) => {
          if (e == true) {
            onDownloadStoreSchedule();
            setOpenScheduleDownloadModal(false);
          } else {
            setOpenScheduleDownloadModal(false);
          }
        }}
        displayText={t("CONFIRM_ACTION", {
          action: t("DOWNLOAD").toLowerCase(),
          entity: t("STORE_SCHEDULE").toLowerCase(),
        })}
        modalTitle={t("CONFIRM_ACTION_TITLE", {
          action: t("DOWNLOAD").toLowerCase(),
        })}
      />
    </div>
  );
}

export default StoreSchedule;
